/**
 * Effective stroke width estimation for arbitrary artwork silhouettes.
 *
 * The shape is rasterized to a small offscreen canvas and the stroke width is
 * derived as 2 × area / perimeter — exact for constant-width strokes, and a
 * sensible "bevelable thickness" for solid shapes (yields the radius for a
 * disk). Used to size the gloss bevel: glyph height is a useless basis for
 * thin script fonts whose strokes are a fraction of a percent of the height.
 */

import { parseSvgViewBox } from './svgDocument';

const MEASURE_TARGET_PX = 240;
const ALPHA_THRESHOLD = 127;
const MEASURE_PADDING_PX = 2;

function countAreaAndPerimeter(data: Uint8ClampedArray, width: number, height: number) {
  let area = 0;
  let perimeter = 0;
  const alphaAt = (x: number, y: number) => data[(y * width + x) * 4 + 3];

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (alphaAt(x, y) <= ALPHA_THRESHOLD) continue;
      area += 1;
      if (
        x === 0 ||
        x === width - 1 ||
        y === 0 ||
        y === height - 1 ||
        alphaAt(x - 1, y) <= ALPHA_THRESHOLD ||
        alphaAt(x + 1, y) <= ALPHA_THRESHOLD ||
        alphaAt(x, y - 1) <= ALPHA_THRESHOLD ||
        alphaAt(x, y + 1) <= ALPHA_THRESHOLD
      ) {
        perimeter += 1;
      }
    }
  }

  return { area, perimeter };
}

function strokeFromCounts(area: number, perimeter: number, scale: number): number | null {
  if (area < 16 || perimeter < 8 || scale <= 0) return null;
  return (2 * area) / perimeter / scale;
}

function makeMeasureCanvas(sourceWidth: number, sourceHeight: number) {
  if (sourceWidth <= 0 || sourceHeight <= 0) return null;

  const scale = MEASURE_TARGET_PX / Math.max(sourceWidth, sourceHeight);
  const width = Math.max(4, Math.ceil(sourceWidth * scale) + MEASURE_PADDING_PX * 2);
  const height = Math.max(4, Math.ceil(sourceHeight * scale) + MEASURE_PADDING_PX * 2);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  return { ctx, scale, width, height };
}

function rasterizeAndCount(
  src: string,
  measure: NonNullable<ReturnType<typeof makeMeasureCanvas>>
): Promise<number | null> {
  const { ctx, scale, width, height } = measure;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        ctx.drawImage(img, MEASURE_PADDING_PX, MEASURE_PADDING_PX, width - MEASURE_PADDING_PX * 2, height - MEASURE_PADDING_PX * 2);
        const { area, perimeter } = countAreaAndPerimeter(
          ctx.getImageData(0, 0, width, height).data,
          width,
          height
        );
        resolve(strokeFromCounts(area, perimeter, scale));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Stroke width of a complete SVG document, in viewBox units. */
export function estimateStrokeWidthFromSvgDocument(svgDocument: string): Promise<number | null> {
  if (!svgDocument?.trim()) return Promise.resolve(null);

  const viewBox = parseSvgViewBox(svgDocument, 100, 100);
  const measure = makeMeasureCanvas(viewBox.width, viewBox.height);
  if (!measure) return Promise.resolve(null);

  const blobUrl = URL.createObjectURL(new Blob([svgDocument], { type: 'image/svg+xml;charset=utf-8' }));

  return rasterizeAndCount(blobUrl, measure).then((stroke) => {
    URL.revokeObjectURL(blobUrl);
    return stroke;
  });
}

/** Stroke width of a raster image's alpha silhouette, in viewBox units. */
export function estimateStrokeWidthFromImageUrl(
  url: string,
  viewBoxWidth: number,
  viewBoxHeight: number
): Promise<number | null> {
  if (!url) return Promise.resolve(null);

  const measure = makeMeasureCanvas(viewBoxWidth, viewBoxHeight);
  if (!measure) return Promise.resolve(null);

  return rasterizeAndCount(url, measure);
}
